// @ts-nocheck
import { parseCssValueUnit } from 'shared/utils/cssUnits'
import {
  BASE_BREAKPOINT,
  BREAKPOINT_MAP,
  BREAKPOINTS,
  CONTENT_MAX_WIDTH,
  MAX_CONTENT_BREAKPOINT,
} from 'views/consts/imgBreakpoints'

import { BreakpointName, SizeObject, WidthAndMargin } from './types'

export const sortSizes = (sizesObject: SizeObject): SizeObject => {
  const result = Object.fromEntries(
    [...BREAKPOINTS, BASE_BREAKPOINT]
      .map(breakpoint => [breakpoint, sizesObject[breakpoint]])
      .filter(([, value]) => value)
  )
  return result
}

const isSizeObjectValueValid = (value: string): boolean => {
  const normalisedValue = value.trim().replace(/\s+/g, ' ')
  const rxSimple =
    /^0*(?=[1-9]|\.\d|[1-9]\.)\d*((\.)|\.(0*[1-9]{1,}\d*)|(0+))?(vw|em|rem)$/
  const rxCalc = /calc\s*\(\s*[1-9]\d*\.?\d*vw\s+-\s+[1-9]\d*\.?\d*(em|rem)/
  return rxSimple.test(normalisedValue) || rxCalc.test(normalisedValue)
}

const validateSizesObject = (sizesObject: SizeObject): void => {
  const values = Object.values(sizesObject)

  const invalidValues = values.filter(size => !isSizeObjectValueValid(size))
  const isAdditionOperator = /[*+/]/
  if (invalidValues.length > 0) {
    const errors = invalidValues.map(value =>
      isAdditionOperator.test(value)
        ? `${value}: only subtraction operator is allowed`
        : value
    )
    const errorMessage =
      `${JSON.stringify(sizesObject)} has the following invalid entries:` +
      `${errors.join(', ')}`
    throw new Error(errorMessage)
  }
}

const getBreakpointWidth = (breakpoint: string): number => {
  const match = /\d*(?=em)/.exec(breakpoint)

  if (match) {
    return parseInt(match[0], 10)
  }

  throw new Error(
    `Cannot extract the width from the supplied parameter ${breakpoint}`
  )
}

const hasCalc = (imgSize: string): boolean => /calc/.test(imgSize)

const getSizeItemWidth = (imgSize: string): number => {
  const rx = /[1-9]\d*\.?\d*(?=vw|em)/
  const match = rx.exec(imgSize)

  if (match) return parseFloat(match[0])

  throw new Error(
    `The getSizeItemWidth function cannot process the parameter that it was supplied with: ${imgSize}`
  )
}

const getSizeItemMargin = (imgSize: string): number => {
  const rxMargin = /[1-9]\d*\.?\d*(?=em|rem)/
  const match = rxMargin.exec(imgSize)

  return match ? parseFloat(match[0]) : 0
}

const hasPotentialToGoNegative = (
  breakpointWidth: number,
  { width, margin }: WidthAndMargin
) => {
  const availableWidth = (width / 100) * breakpointWidth - margin

  if (availableWidth > 0) {
    return false
  }

  return true
}

const getSafeBreakpoint = ({ width, margin }: WidthAndMargin): string => {
  const newWidth = ((100 / width) * margin + 0.1).toFixed(1)

  return `(min-width: ${newWidth}em)`
}

const validateGeneratedSizes = (
  sizesObject: SizeObject,
  sizes: string[]
): void => {
  const rx = /[1-9]\d*\.?\d*(?=em)/
  const widths = sizes.map(size => {
    const width = rx.exec(size)
    return width ? parseFloat(width[0]) : null
  })

  const invalidViewportWidths = []
  for (let i = 1; i < widths.length; i += 1) {
    if (widths[i] > widths[i - 1]) {
      invalidViewportWidths.push(
        `Breakpoint ${widths[i]}em (original item: ${sizes[i]})` +
          `is larger than previous ${widths[i - 1]}em` +
          `(original item: ${sizes[i - 1]})` +
          `, which is not allowed.`
      )
    }
  }
  if (invalidViewportWidths.length > 0) {
    const errorMessage =
      'The submitted sizes object is not valid: ' +
      `${JSON.stringify(sizesObject)}` +
      ' It resulted in the string: ' +
      `${sizes.join(', ')}` +
      ' This string has the following errors: ' +
      `${invalidViewportWidths.join(', ')}`
    throw new Error(errorMessage)
  }
}

const convertToEm = (sizes: SizeObject) => {
  const convertedSizes = Object.entries(sizes).map(size => [
    size[0],
    size[1].replace(/rem/g, 'em'),
  ])

  return Object.fromEntries(convertedSizes)
}

const computeWidth = (width: string): string => {
  const rx = /[1-9]\d*\.?\d*em/g
  const operands = width.match(rx)

  if (operands && operands.length === 2) {
    const units = operands.map(operand => operand.replace(/\.|\d/g, ''))
    if (units[0] === units[1]) {
      const dimensions = operands.map(operand =>
        operand.replace(/(em|rem)/g, '')
      )
      return `${parseFloat(dimensions[0]) - parseFloat(dimensions[1])}${
        units[0]
      }`
    }
  }

  return width
}

const convertVms = (
  value: string,
  base: { value: number; unit: string }
): string => {
  const convertedWidth: string = value.replace(
    /[0-9]*\.?[0-9]+vw/g,
    valueWithUnit => {
      const vw = parseCssValueUnit(valueWithUnit)
      return `${(vw.value / 100) * base.value}${base.unit}`
    }
  )

  return hasCalc(convertedWidth) ? computeWidth(convertedWidth) : convertedWidth
}

/**
 * Add a max-content breakpoint, if largest breakpoint is not max-content and it uses vm unit
 */
const clampSizesOnMaxContent = (sortedSizesObject: SizeObject): SizeObject => {
  const largestSize = Object.values(sortedSizesObject)[0]
  const contentMaxWidth = parseCssValueUnit(CONTENT_MAX_WIDTH)
  const maxContentSize = convertVms(largestSize, contentMaxWidth)

  if (maxContentSize !== largestSize) {
    return {
      [MAX_CONTENT_BREAKPOINT]: maxContentSize,
      ...sortedSizesObject,
    }
  }

  return sortedSizesObject
}

export const transformSizes = (sizesObject: SizeObject): string | null => {
  if (Object.keys(sizesObject).length === 0) {
    return null
  }

  validateSizesObject(sizesObject)

  const sizesObjectInEm = convertToEm(sizesObject)

  let sortedSizesObject = sortSizes(sizesObjectInEm)
  if (!(MAX_CONTENT_BREAKPOINT in sortedSizesObject)) {
    sortedSizesObject = clampSizesOnMaxContent(sortedSizesObject)
  }

  const sizes = []

  Object.entries(sortedSizesObject).forEach(
    ([breakpointName, imgSize]: [BreakpointName, string]): void => {
      if (breakpointName === BASE_BREAKPOINT) {
        const width = getSizeItemWidth(imgSize)
        const margin = getSizeItemMargin(imgSize)
        if (hasCalc(imgSize)) {
          sizes.push(`${getSafeBreakpoint({ width, margin })} ${imgSize}`)
          sizes.push(`${getSizeItemWidth(imgSize)}vw`)
        } else {
          sizes.push(imgSize)
        }
      } else {
        const breakpoint = BREAKPOINT_MAP[breakpointName].replace(/rem/, 'em')
        const breakpointWidth = getBreakpointWidth(breakpoint)

        if (hasCalc(imgSize)) {
          const width = getSizeItemWidth(imgSize)
          const margin = getSizeItemMargin(imgSize)
          const isNegativeWidth = hasPotentialToGoNegative(breakpointWidth, {
            width,
            margin,
          })

          if (isNegativeWidth) {
            sizes.push(`${getSafeBreakpoint({ width, margin })} ${imgSize}`)
            sizes.push(`${breakpoint} ${width}vw`)
          } else {
            sizes.push(`${breakpoint} ${imgSize}`)
          }
        } else {
          sizes.push(`${breakpoint} ${imgSize}`)
        }
      }
    }
  )

  validateGeneratedSizes(sizesObject, sizes)

  return sizes.join(',')
}
